import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Hook for backend pagination with "load more" functionality
 * Accumulates items from multiple pages and displays all accumulated items
 * 
 * @param {Function} fetchFunction - Async function that receives params and returns { items, meta } or error
 * @param {Object} initialParams - Initial parameters for the first request
 * @param {Object} options - Additional options
 * @param {number} options.itemsPerPage - Items per page from backend (default: 20)
 * @param {Function} options.onSuccess - Callback when request is successful
 * @param {Function} options.onError - Callback when there's an error
 * @param {boolean} options.autoLoadFirstPage - Automatically load first page on mount (default: true)
 * @returns {Object} - { items, isLoading, error, meta, hasMore, currentPage, totalItems, totalPages, loadMore, refresh, reset, loadFirstPage, updateParams }
 */
export function useBackendPagination(
  fetchFunction,
  initialParams = {},
  options = {}
) {
  const {
    itemsPerPage = 8,
    onSuccess = null,
    onError = null,
    autoLoadFirstPage = true,
  } = options;

  // State for accumulated items from backend
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);

  // Refs to control pagination
  const currentPageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const paramsRef = useRef(initialParams);
  const isInitialLoadRef = useRef(true);
  const isLoadingRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Update hasMore based on meta
  useEffect(() => {
    if (meta) {
      hasMoreRef.current = meta.has_next || false;
    }
  }, [meta]);

  /**
   * Internal function to make the request
   */
  const fetchPage = useCallback(async (page, params, append = false) => {
    // Check if already loading using ref to avoid closure issues
    if (isLoadingRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const requestParams = {
        ...paramsRef.current,
        ...params,
        page,
        page_size: itemsPerPage,
      };

      const result = await fetchFunction(requestParams);

      // If fetchFunction returns an error (object with errorType)
      if (result && result.errorType) {
        throw result;
      }

      // Extract items and meta from response
      // Supports different formats: { items, meta }, { body, meta }, { groups, meta }
      const responseItems = result.items || result.body || result.groups || [];
      const responseMeta = result.meta || {};

      if (append) {
        // Accumulate items (load more)
        setItems((prev) => [...prev, ...responseItems]);
      } else {
        // Replace items (refresh/reset)
        setItems(responseItems);
      }

      setMeta(responseMeta);
      currentPageRef.current = page;
      hasMoreRef.current = responseMeta.has_next || false;

      if (onSuccess) {
        onSuccess(responseItems, responseMeta);
      }

      return { items: responseItems, meta: responseMeta };
    } catch (err) {
      const errorObj = err.errorType ? err : {
        errorType: 'UNKNOWN_ERROR',
        message: err.message || 'An error occurred',
        error: err
      };

      setError(errorObj);
      
      if (onError) {
        onError(errorObj);
      }

      throw errorObj;
    } finally {
      setIsLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [fetchFunction, itemsPerPage, onSuccess, onError]);

  /**
   * Load the first page (or refresh)
   */
  const loadFirstPage = useCallback(async (newParams = {}) => {
    paramsRef.current = { ...paramsRef.current, ...newParams };
    currentPageRef.current = 1;
    return await fetchPage(1, newParams, false);
  }, [fetchPage]);

  /**
   * Load the next page (load more)
   * Loads the next page from backend and accumulates items
   */
  const loadMore = useCallback(async () => {
    // If already loading or no more pages, do nothing
    if (isLoadingRef.current || !hasMoreRef.current) return;

    const nextPage = currentPageRef.current + 1;
    return await fetchPage(nextPage, {}, true);
  }, [fetchPage]);

  /**
   * Refresh the current list (maintains current page)
   */
  const refresh = useCallback(async (newParams = {}) => {
    paramsRef.current = { ...paramsRef.current, ...newParams };
    return await fetchPage(currentPageRef.current, newParams, false);
  }, [fetchPage]);

  /**
   * Reset completely (go back to page 1)
   */
  const reset = useCallback(async (newParams = {}) => {
    // Merge initial params with new params, preserving order_by and order_direction
    paramsRef.current = { ...initialParams, ...newParams };
    currentPageRef.current = 1;
    return await fetchPage(1, paramsRef.current, false);
  }, [fetchPage, initialParams]);

  // Automatically load first page
  useEffect(() => {
    if (autoLoadFirstPage && isInitialLoadRef.current) {
      loadFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  return {
    // All accumulated items from backend
    items,
    
    // State
    isLoading,
    error,
    meta,
    
    // Pagination information
    hasMore: hasMoreRef.current,
    currentPage: currentPageRef.current,
    totalItems: meta?.total_items || 0,
    totalPages: meta?.total_pages || 0,
    
    // Actions
    loadMore,
    refresh,
    reset,
    loadFirstPage,
    
    // Utilities
    updateParams: (newParams) => {
      paramsRef.current = { ...paramsRef.current, ...newParams };
    },
  };
}

